/**
 * Etapa 2 — Proteções instaladas. Cada proteção é gravada na hora (depende do id da máquina).
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Box, Button, Chip, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataTable } from '../../../../../components';
import { machineService, MachineProtection, MachineProtectionType, MACHINE_PROTECTION_TYPES } from '../../../../../services/machineService';
import { ConfirmDialog } from '../ConfirmDialog';
import { ListStepProps, PROTECTION_STATE_LABELS, PROTECTION_TYPE_LABELS, StepHandle } from '../MachineWizard.types';
import {
  EMPTY_PROTECTION_DRAFT,
  errorMessage,
  ProtectionDraft,
  ProtectionErrors,
  protectionToPayload,
  validateProtection,
} from '../machineWizardValidation';

const fieldSx = { '& .MuiFormHelperText-root': { fontSize: '1rem' } };

const draftFrom = (item: MachineProtection): ProtectionDraft => ({
  protectionType: item.protectionType,
  description: item.description ?? '',
  location: item.location ?? '',
  state: item.state,
  observations: item.observations ?? '',
});

export const ProtectionsStep = forwardRef<StepHandle, ListStepProps>(
  ({ machineId, canEdit, onDirtyChange, onSaved }, ref) => {
    const [items, setItems] = useState<MachineProtection[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [draft, setDraft] = useState<ProtectionDraft | null>(null);
    const [baseline, setBaseline] = useState<ProtectionDraft>(EMPTY_PROTECTION_DRAFT);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [errors, setErrors] = useState<ProtectionErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [removing, setRemoving] = useState<MachineProtection | null>(null);

    const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(baseline);

    useEffect(() => {
      onDirtyChange(dirty);
    }, [dirty, onDirtyChange]);

    const load = useCallback(async () => {
      try {
        setItems(await machineService.getProtections(machineId));
        setError(null);
      } catch (err: unknown) {
        setError(errorMessage(err, 'Não foi possível carregar as proteções.'));
      } finally {
        setLoading(false);
      }
    }, [machineId]);

    useEffect(() => {
      load();
    }, [load]);

    const openDraft = (item?: MachineProtection) => {
      const initial = item ? draftFrom(item) : EMPTY_PROTECTION_DRAFT;
      setBaseline(initial);
      setDraft(initial);
      setEditingId(item ? item.id : null);
      setErrors({});
    };

    const closeDraft = () => {
      setDraft(null);
      setEditingId(null);
      setErrors({});
    };

    const save = useCallback(async (): Promise<boolean> => {
      if (!draft) return true;
      const found = validateProtection(draft);
      setErrors(found);
      if (Object.keys(found).length > 0) return false;

      setSubmitting(true);
      try {
        const payload = protectionToPayload(draft);
        if (editingId !== null) await machineService.updateProtection(machineId, editingId, payload);
        else await machineService.createProtection(machineId, payload);
        await load();
        closeDraft();
        onSaved('Proteção guardada.');
        return true;
      } catch (err: unknown) {
        setError(errorMessage(err, 'Não foi possível guardar a proteção.'));
        return false;
      } finally {
        setSubmitting(false);
      }
    }, [draft, editingId, machineId, load, onSaved]);

    useImperativeHandle(
      ref,
      () => ({
        salvarPendente: async () => {
          if (!draft) return true;
          if (!dirty) {
            closeDraft();
            return true;
          }
          return save();
        },
      }),
      [draft, dirty, save],
    );

    const confirmRemoval = async () => {
      if (!removing) return;
      try {
        await machineService.deleteProtection(machineId, removing.id);
        setRemoving(null);
        await load();
        onSaved('Proteção removida.');
      } catch (err: unknown) {
        setRemoving(null);
        setError(errorMessage(err, 'Não foi possível remover a proteção.'));
      }
    };

    return (
      <Box>
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          Proteções que a máquina tem hoje
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Registre o que está instalado e se está funcionando. O que faltar vira adequação na próxima etapa.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DataTable<MachineProtection>
          embedded
          loading={loading}
          rows={items}
          getRowId={(row) => row.id}
          emptyState={
            canEdit
              ? {
                  title: 'Nenhuma proteção registrada',
                  description: 'Use "Adicionar proteção" para registrar cada proteção instalada. Se a máquina não tem nenhuma, siga em frente: isso vira adequação.',
                }
              : {
                  title: 'Nenhuma proteção registrada',
                  description: 'Esta máquina ainda não tem proteções registradas.',
                }
          }
          columns={[
            {
              id: 'protection',
              label: 'Proteção',
              primary: true,
              render: (row) =>
                row.protectionType === 'OUTRA' && row.description
                  ? `Outra: ${row.description}`
                  : PROTECTION_TYPE_LABELS[row.protectionType],
            },
            { id: 'location', label: 'Onde atua', render: (row) => row.location ?? '—' },
            {
              id: 'state',
              label: 'Estado',
              render: (row) => (
                <Chip
                  size="medium"
                  variant="outlined"
                  color={row.state === 'CONFORME' ? 'success' : 'warning'}
                  label={PROTECTION_STATE_LABELS[row.state]}
                />
              ),
            },
            { id: 'observations', label: 'Observação', hideOnMobile: true, render: (row) => row.observations ?? '' },
          ]}
          actions={
            canEdit
              ? [
                  { icon: <EditIcon />, tooltip: 'Editar proteção', onClick: (row) => openDraft(row) },
                  { icon: <DeleteIcon />, tooltip: 'Remover proteção', color: 'error', onClick: (row) => setRemoving(row) },
                ]
              : undefined
          }
        />

        {canEdit && draft === null && (
          <Button variant="outlined" onClick={() => openDraft()} sx={{ mt: 2, minHeight: 44 }}>
            Adicionar proteção
          </Button>
        )}

        {!canEdit && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Você pode ver as proteções, mas não alterá-las. Peça a quem administra o seu acesso.
          </Alert>
        )}

        {canEdit && draft !== null && (
          <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
              {editingId !== null ? 'Editar proteção' : 'Nova proteção'}
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Proteção"
                  value={draft.protectionType}
                  onChange={(event) =>
                    setDraft({ ...draft, protectionType: event.target.value as MachineProtectionType | '' })
                  }
                  error={Boolean(errors.protectionType)}
                  helperText={errors.protectionType}
                  disabled={submitting}
                  sx={fieldSx}
                >
                  <MenuItem value="">Escolha na lista</MenuItem>
                  {MACHINE_PROTECTION_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {PROTECTION_TYPE_LABELS[type]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Onde atua"
                  value={draft.location}
                  onChange={(event) => setDraft({ ...draft, location: event.target.value })}
                  error={Boolean(errors.location)}
                  helperText={errors.location || 'Ex.: zona de prensagem'}
                  disabled={submitting}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  value={draft.state}
                  onChange={(event) => setDraft({ ...draft, state: event.target.value as ProtectionDraft['state'] })}
                  disabled={submitting}
                  sx={fieldSx}
                >
                  {Object.entries(PROTECTION_STATE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {draft.protectionType === 'OUTRA' && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    required
                    label="Descreva a proteção"
                    value={draft.description}
                    onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                    error={Boolean(errors.description)}
                    helperText={errors.description || 'Como você chamaria essa proteção?'}
                    disabled={submitting}
                    sx={fieldSx}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Observação"
                  value={draft.observations}
                  onChange={(event) => setDraft({ ...draft, observations: event.target.value })}
                  disabled={submitting}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button variant="outlined" onClick={save} disabled={submitting} sx={{ minHeight: 44 }}>
                Guardar proteção
              </Button>
              <Button variant="text" onClick={closeDraft} disabled={submitting} sx={{ minHeight: 44 }}>
                Descartar
              </Button>
            </Box>
          </Paper>
        )}

        <ConfirmDialog
          open={removing !== null}
          title="Remover esta proteção?"
          message="Ela some do cadastro da máquina e do laudo."
          confirmLabel="Remover"
          cancelLabel="Manter"
          onConfirm={confirmRemoval}
          onCancel={() => setRemoving(null)}
        />
      </Box>
    );
  },
);

ProtectionsStep.displayName = 'ProtectionsStep';

export default ProtectionsStep;
